import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {callWithDataModifications} from 'audit/DataModificationsModal';
import {pluralize} from 'BenchlingUtil';
import {Container} from 'constants/SampleConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  composeTestOrderBulkEventEventMetadata,
  TEST_ORDER_BULK_UPDATE_ENDED_EVENT,
  TEST_ORDER_BULK_UPDATE_STARTED_EVENT,
} from 'performance/processSampleTestsEvents';
import {bulkUpdateTestOrders} from 'sample-tests/network/TestOrderDAO';
import {TestOrderDataModAction} from 'sample-tests/test-orders/TestOrderDomainTypes';
import TestOrderDisplayTable from 'sample-tests/ui/test-orders/modals/TestOrderDisplayTable';
import {ContainerSearchInput} from 'sample-tests/ui/test-orders/modals/useTestOrderCreationTable';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';
import {TABLE_KEYS} from 'ui-library/scrollable/ScrollableTableConstants';

import {TestOrderListingGraphQL} from '../../../../../graphql-fragments/sample_tests/__generated/TestOrderListingGraphQL';

export interface BulkUpdateTestOrderContainerModalProps {
  testOrders: Array<TestOrderListingGraphQL>;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

export default function BulkUpdateTestOrderContainerModal(
  props: BulkUpdateTestOrderContainerModalProps
): JSX.Element {
  const {testOrders, onRequestClose, onRequestSubmit} = props;
  const testOrderIds = testOrders.map((testOrder) => testOrder.id);
  assert(testOrders.length > 0, 'Expected at least one test order');
  assert(
    testOrders.every((testOrder) => testOrder.sample.id === testOrders[0].sample.id),
    'Expected all test orders to have the same sample'
  );
  const [newContainer, setNewContainer] = useState<Container | null>(null);
  const testOrdersString = `${testOrderIds.length} test ${pluralize(testOrderIds.length, 'order')}`;

  async function updateTestOrdersWithDataModifications(): Promise<void> {
    assert(newContainer != null, 'Expected submit to be disabled for null container');
    await callWithDataModifications({
      changeSummary: `Updating ${testOrdersString}`,
      onPerformAction: async (dataModsHeader?: DataModificationsHeader) => {
        logProfilerTimingEvent(TEST_ORDER_BULK_UPDATE_STARTED_EVENT);
        await bulkUpdateTestOrders(
          testOrderIds,
          {containerId: newContainer.id},
          {actionOrigin: ActionOrigin.SAMPLE_TESTS_BULK_UPDATE_TEST_ORDER_CONTAINER_MODAL},
          dataModsHeader
        );
        logProfilerTimingEvent(
          TEST_ORDER_BULK_UPDATE_ENDED_EVENT,
          composeTestOrderBulkEventEventMetadata(
            testOrders.length,
            ActionOrigin.SAMPLE_TESTS_BULK_UPDATE_TEST_ORDER_CONTAINER_MODAL
          )
        );
        notifications.success(`${testOrdersString} updated`);
        onRequestSubmit();
      },
      apiIds: testOrderIds,
      analyticsParams: {
        module: 'SAMPLE_TESTS',
        actionOrigin: ActionOrigin.SEARCH_EXPANDED_SEARCH_TABLE,
        modifiedAction: TestOrderDataModAction.CONTAINER_CHANGE,
        numModifiedTotalItems: testOrderIds.length,
        entryId: null,
      },
      useProjectLevelDataModsLogic: true,
    });
  }

  return (
    <Modal
      title={<div className={css(styles.modalTitle)}>Update container</div>}
      mediumLarge={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={updateTestOrdersWithDataModifications}
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'Update', submit: true, disabled: newContainer == null},
      ]}
      closeable={true}
      closeableOnEscapeKey={false}
      closeableOnBackgroundClick={false}
      expandHeight={true}
    >
      <FieldLabel isRequired={true} label="New container" />
      <ContainerSearchInput
        sampleId={testOrders[0].sample.id}
        rowId="bulk-update-new-container"
        disabled={false}
        autoFocus={false}
        handleContainerChange={(_, container) => {
          setNewContainer(container);
        }}
        fetchSelectedContainer={() => (newContainer != null ? [newContainer] : [])}
      />
      <div className={css(styles.modalText)}>{`${testOrdersString} selected`}</div>
      <TestOrderDisplayTable
        testOrders={testOrders}
        tableKey={TABLE_KEYS.UPDATE_TEST_ORDER_CONTAINER_TABLE}
        excludeColumns={['status']}
      />
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalText: {
    ...FONT_PROXIMA_13,
    paddingBottom: '16px',
    paddingTop: '16px',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 400,
  },
});
